import { MutationActions, QueryKeys, useEntityMutation } from "@/api/core";
import { useApi } from "@/hooks";
import { type Group, groupRelationshipSchema, groupSchema } from "@/schemas";
import toast from "@/toast";
import {
  findEntityById,
  prependPageItem,
  removePageItem,
  updateQueryData,
} from "@/utils/queries";

import type { ApiError } from "@/types/api";
import type { AxiosError } from "axios";

interface Variables {
  id: string;
}

interface UseJoinGroupProps {
  shouldKeepSuggestedCache?: boolean;
}

function useJoinGroup(
  props: UseJoinGroupProps = { shouldKeepSuggestedCache: false },
) {
  const api = useApi();

  const { shouldKeepSuggestedCache } = props;

  return useEntityMutation({
    globalQueryKey: QueryKeys.groupRelationships.global,
    action: MutationActions.UPDATE,
    schema: groupRelationshipSchema,
    mutationFn: (variables: Variables) =>
      api.post(`/api/v1/groups/${variables.id}/join`),
    onSuccess(_data, variables) {
      const group = findEntityById(
        QueryKeys.groups.global,
        variables.id,
      ) as Group;
      if (group.locked) {
        prependPageItem(QueryKeys.groups.pending(), group);
      } else {
        // Update group members count
        updateQueryData(
          QueryKeys.groups.global,
          { ...group, members_count: group.members_count + 1 },
          groupSchema,
        );
        // Initial page for My Groups
        prependPageItem(QueryKeys.groups.searchBy("", 20), group);
      }

      // Remove group from suggested groups
      if (!shouldKeepSuggestedCache) {
        removePageItem(
          QueryKeys.groups.suggested(),
          variables.id,
          (o: any, n: any) => o.id === n,
        );
      }
    },
    onError(error: AxiosError<ApiError>) {
      if (error.response?.data?.errors) {
        const message = error.response.data.errors[0].error_message;
        toast.error(message);
      }
    },
  });
}

export { useJoinGroup };
